import { API_CONFIG } from '../config/api.config';
import { PageResponse, PortfolioItem, ProviderProfile, ProviderProfileRequest } from '../types';
import { apiService } from './api.service';
import { peekApi } from '../prerender/apiCache';

export interface PublicFeatures {
  publicProviderDirectContact: boolean;
}

export interface PublicProviderContact {
  displayPhone: string;
  telUrl: string;
  whatsappUrl: string;
}

export type ProviderSearchParams = {
  categoryId?: string;
  q?: string;
  verifiedOnly?: boolean;
  certifiedOnly?: boolean;
  available?: boolean;
  lat?: number;
  lon?: number;
  radiusKm?: number;
  minRating?: number;
  page?: number;
  size?: number;
  sort?: string;
};

export const providerService = {
  /** First-render reads of prerendered responses (undefined when not prerendered). */
  peekPublicFeatures: (): PublicFeatures | undefined => peekApi<PublicFeatures>('/features/public'),
  peekProviderById: (id: string): ProviderProfile | undefined => peekApi<ProviderProfile>(API_CONFIG.ENDPOINTS.PROVIDERS.BY_ID(id)),
  peekSearchProviders: (params: ProviderSearchParams): PageResponse<ProviderProfile> | undefined =>
    peekApi<PageResponse<ProviderProfile>>(API_CONFIG.ENDPOINTS.PROVIDERS.SEARCH, params as Record<string, unknown>),

  async getPublicFeatures(): Promise<PublicFeatures> {
    const response = await apiService.get<PublicFeatures>('/features/public');
    return response.data;
  },

  async getPublicContact(providerId: string): Promise<PublicProviderContact> {
    const response = await apiService.get<PublicProviderContact>(
      `/providers/${encodeURIComponent(providerId)}/public-contact`
    );
    return response.data;
  },

  async getMyProfile(): Promise<ProviderProfile> {
    const response = await apiService.get<ProviderProfile>(
      API_CONFIG.ENDPOINTS.PROVIDERS.MY_PROFILE
    );
    return response.data;
  },

  async getProviderById(id: string): Promise<ProviderProfile> {
    const response = await apiService.get<ProviderProfile>(
      API_CONFIG.ENDPOINTS.PROVIDERS.BY_ID(id)
    );
    return response.data;
  },

  // Alias for getProviderById
  async getProviderProfile(id: string): Promise<ProviderProfile> {
    return this.getProviderById(id);
  },

  async createProfile(data: ProviderProfileRequest): Promise<ProviderProfile> {
    const response = await apiService.post<ProviderProfile>(
      API_CONFIG.ENDPOINTS.PROVIDERS.CREATE,
      data
    );
    return response.data;
  },

  async updateProfile(data: Partial<ProviderProfileRequest>): Promise<ProviderProfile> {
    const response = await apiService.put<ProviderProfile>(
      API_CONFIG.ENDPOINTS.PROVIDERS.UPDATE,
      data
    );
    return response.data;
  },

  // Legacy alias - checks if profile exists and calls correct method
  async createOrUpdateProfile(data: ProviderProfileRequest): Promise<ProviderProfile> {
    try {
      // Try to get existing profile first
      await this.getMyProfile();
      // If exists, update
      return this.updateProfile(data);
    } catch {
      // If not exists, create
      return this.createProfile(data);
    }
  },



  async searchProviders(params: ProviderSearchParams): Promise<PageResponse<ProviderProfile>> {
    const response = await apiService.get<PageResponse<ProviderProfile>>(
      API_CONFIG.ENDPOINTS.PROVIDERS.SEARCH,
      params as Record<string, unknown>
    );
    return response.data;
  },

  async suggestProviders(params: {
    q: string;
    cityId?: string;
    lat?: number;
    lon?: number;
  }): Promise<ProviderProfile[]> {
    const response = await apiService.get<ProviderProfile[]>(
      API_CONFIG.ENDPOINTS.PROVIDERS.SUGGEST,
      params as Record<string, unknown>
    );
    return response.data;
  },
  async addPortfolioItem(data: { photoUrl: string; title?: string; description?: string }): Promise<PortfolioItem> {
    const response = await apiService.post<PortfolioItem>(
      API_CONFIG.ENDPOINTS.PROVIDERS.PORTFOLIO_ADD,
      data
    );
    return response.data;
  },

  async removePortfolioItem(itemId: string): Promise<void> {
    await apiService.delete(
      API_CONFIG.ENDPOINTS.PROVIDERS.PORTFOLIO_REMOVE(itemId)
    );
  },

  async getProviderPortfolio(providerId: number): Promise<PortfolioItem[]> {
    const response = await apiService.get<PortfolioItem[]>(
      `providers/${providerId}/portfolio`
    );
    return response.data;
  },

  async deletePortfolioItem(itemId: number): Promise<void> {
    await apiService.delete(`providers/me/portfolio/${itemId}`);
  },

  async requestVerification(data: {
    cinNumber?: string;
    documentFrontUrl?: string;
    documentBackUrl?: string;
  }): Promise<void> {
    await apiService.post('provider/verification/request', data);
  },

  async deleteMyProfile(): Promise<void> {
    await apiService.delete('providers/me');
  },
};


